import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEmbeddedSignupByPhone } from '../../../api/login.service';

interface MarketingMessage {
  id: string;
  templateName: string;
  phoneNumber: string;
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'pending';
  sentAt: string;
  deliveredAt?: string;
  readAt?: string;
  recipientCount: number;
  successCount: number;
  failureCount: number;
  templateId: string;
  wabaId: string;
}

interface MarketingMessagesResponse {
  data?: MarketingMessage[];
  paging?: {
    cursors?: { before?: string; after?: string };
    next?: string;
    previous?: string;
  };
}

export default function MarketingMessagesList() {
  const navigate = useNavigate();
  
  // Get wabaId from session storage instead of URL params
  const wabaId = sessionStorage.getItem('wabaId');
  const [marketingMessages, setMarketingMessages] = useState<MarketingMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [after, setAfter] = useState<string | undefined>(undefined);
  const [before, setBefore] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<{ after?: string; before?: string }>({});

  // Fetch marketing messages from the API
  const fetchMarketingMessages = async ({ after, before }: { after?: string; before?: string } = {}) => {
    setLoading(true);
    setError(null);
    
    // Clear any existing data first
    setMarketingMessages([]);
    
    try {
      // Check if user has access by calling the embedded signup API
      const displayPhoneNumber = localStorage.getItem('display_phone_number');
      if (!displayPhoneNumber) {
        console.log('No display phone number found');
        setMarketingMessages([]);
        return;
      }

      // Check embedded signup records using the proper API function
      try {
        const embeddedData = await getEmbeddedSignupByPhone(displayPhoneNumber);
        console.log('Marketing Messages - Embedded data:', embeddedData);
        
        // Check if records array is empty
        if (embeddedData?.records && Array.isArray(embeddedData.records)) {
          if (embeddedData.records.length === 0) {
            console.log('No WhatsApp Business accounts found for this user');
            setMarketingMessages([]);
            return;
          }
        } else {
          // If no records property, show no accounts message
          console.log('No WhatsApp Business accounts found for this user');
          setMarketingMessages([]);
          return;
        }
      } catch (apiError) {
        console.error('Marketing Messages - API Error:', apiError);
        console.log('Could not fetch WhatsApp Business data, continuing without it');
        setMarketingMessages([]);
        return;
      }

      // For now, show empty state since marketing messages API might not be ready
      setMarketingMessages([]);
      setError('No marketing messages found. Marketing campaigns will appear here once you start sending them.');
      
      // Set cursors if your API supports pagination
      setCursor({
        after: undefined,
        before: undefined,
      });
      
    } catch (err: any) {
      console.error('Marketing Messages - General Error:', err);
      setMarketingMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketingMessages({ after, before });
  }, [after, before]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered') return { label: 'Delivered', className: 'bg-green-100 text-green-700 border-green-200' };
    if (s === 'sent') return { label: 'Sent', className: 'bg-blue-100 text-blue-700 border-blue-200' };
    if (s === 'read') return { label: 'Read', className: 'bg-purple-100 text-purple-700 border-purple-200' };
    if (s === 'failed') return { label: 'Failed', className: 'bg-red-100 text-red-700 border-red-200' };
    if (s === 'pending') return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    return { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  };

  const getSuccessRate = (success: number, total: number) => {
    if (total === 0) return '0%';
    return `${Math.round((success / total) * 100)}%`;
  };

  if (error) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold">Marketing Messages</h1>
              <p className="text-sm text-gray-500">Track your sent marketing campaigns and their performance</p>
              {wabaId && (
                <p className="text-xs text-gray-400 mt-1">WABA ID: {wabaId}</p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/events/whatsapp-marketing-send')}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm hover:bg-blue-700"
              >
                Send Marketing Message
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="text-left text-sm text-gray-600">
                  <th className="px-5 py-3">Template Name</th>
                  <th className="px-5 py-3">Phone Number</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Sent At</th>
                  <th className="px-5 py-3">Recipients</th>
                  <th className="px-5 py-3">Success Rate</th>
                  <th className="px-5 py-3">Last Update</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      {[...Array(7)].map((__, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                )}

                {!loading && marketingMessages.length === 0 && (
                  <tr className="border-t border-gray-100">
                    <td className="px-5 py-10 text-center text-sm text-gray-500" colSpan={7}>
                      No marketing messages found.
                    </td>
                  </tr>
                )}

                {!loading && marketingMessages.map((message) => {
                  const statusBadge = getStatusBadge(message.status);
                  const successRate = getSuccessRate(message.successCount, message.recipientCount);
                  const lastUpdate = message.readAt || message.deliveredAt || message.sentAt;
                  
                  return (
                    <tr key={message.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <div className="font-medium text-gray-900">{message.templateName}</div>
                        <div className="text-xs text-gray-500">ID: {message.templateId}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        {message.phoneNumber}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        {formatDate(message.sentAt)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm text-gray-700">{message.recipientCount}</div>
                        <div className="text-xs text-gray-500">
                          {message.successCount} success, {message.failureCount} failed
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          message.successCount / message.recipientCount >= 0.8 
                            ? 'bg-green-100 text-green-700' 
                            : message.successCount / message.recipientCount >= 0.6
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {successRate}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        {formatDate(lastUpdate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: paging */}
          {marketingMessages.length > 0 && (
            <div className="flex items-center justify-between px-5 py-3 text-sm text-gray-600">
              <div>
                {marketingMessages.length} marketing messages shown
              </div>
              <div className="space-x-2">
                <button
                  className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-50"
                  onClick={() => setBefore(cursor.before)}
                  disabled={!cursor.before || loading}
                >
                  ← Prev
                </button>
                <button
                  className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-50"
                  onClick={() => setAfter(cursor.after)}
                  disabled={!cursor.after || loading}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
